'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { PageHeader } from '@/components/common/page-header';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { useToast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/organization-context';
import { apiClient } from '@/lib/api-client';
import { OctDto, PetrohqApiResponseListOctDto } from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { useCallback, useEffect, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';

export function Oct() {
  const { toast } = useToast();
  const { getAllStores } = useOrganization();
  // 온디맨드 방식: API 호출 시점에만 필터 상태 읽기

  // 상태 관리
  const [octData, setOctData] = useState<OctDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // OCT 데이터 가져오기 함수
  const fetchOctData = useCallback(async () => {
    // API 호출 시점에 현재 필터 상태 읽기
    const filters = useFilterStore.getState();

    setIsLoading(true);
    try {
      const storeCodeList =
        filters.store.length > 0 ? filters.store : getAllStores();

      // 중앙집중식 파라미터 구축 사용
      const params = filters.buildApiParams('oct', storeCodeList);

      const response = await apiClient.get('sales/oct', {
        searchParams: params,
      });
      const data = (await response.json()) as PetrohqApiResponseListOctDto;

      if (data.result) {
        setOctData(data.result);
      }
    } catch (error) {
      console.error('OCT data fetch error:', error);
      toast({
        title: '조회 실패',
        description: 'OCT 데이터를 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [getAllStores, toast]);

  // 조회 버튼 클릭 핸들러
  const handleSearch = () => {
    fetchOctData();
  };

  // 초기 데이터 로드 (필터 변경 시 자동 리로드 제거)
  useEffect(() => {
    fetchOctData();
  }, []); // 빈 의존성 배열로 변경하여 초기 로드만 수행

  return (
    <div className='h-full'>
      <ExpandableCard
        title='OCT 현황'
        breadcrumb={
          <Breadcrumb items={menuBreadcrumbItems('oct')} />
        }
        pageHeader={
          <PageHeader
            onSearch={handleSearch}
            isLoading={isLoading}
          />
        }
        fill
      >
        <div
          className='border rounded-lg flex-1 min-h-0'
        >
          <div className='w-full h-full'>
            <RealGridReact
              gridProps={{
                display: { fitStyle: RealGrid.GridFitStyle.FILL },
                checkBar: { visible: false },
                stateBar: { visible: false },
                edit: { readOnly: true, editable: false },
                footer: { visible: false },
              }}
              rows={octData}
            >
              <RGDataField
                fieldName={'compCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'compName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'issueCode'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'cdate'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'dtime'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'dealCode'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'dealName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'cardNo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'cType'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'cStatus'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'oilType'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'oilName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'rQty'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'rAmount'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'validDate'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'udate'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'utime'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'bigo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'bfIssueCode'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataColumn fieldName={'storeName'} header={{ text: '매장' }} />
              <RGDataColumn
                fieldName={'issueCode'}
                header={{ text: '보관증번호' }}
                width={100}
              />
              <RGDataColumn
                fieldName={'cdate'}
                header={{ text: '발급일자' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'dtime'}
                header={{ text: '발급시간' }}
                width={70}
              />
              <RGDataColumn
                fieldName={'dealCode'}
                header={{ text: '코드' }}
                width={60}
              />
              <RGDataColumn
                fieldName={'dealName'}
                header={{ text: '거래처명' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'cardNo'}
                header={{ text: '차량번호' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'cType'}
                header={{ text: '보관증유형' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'cStatus'}
                header={{ text: '상태' }}
                width={60}
              />
              <RGDataColumn
                fieldName={'oilType'}
                header={{ text: '유종코드' }}
                width={70}
              />
              <RGDataColumn
                fieldName={'oilName'}
                header={{ text: '유종명' }}
                width={70}
              />
              <RGDataColumn
                fieldName={'rQty'}
                header={{ text: '수량' }}
                width={70}
                numberFormat={'#,##0.###'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'rAmount'}
                header={{ text: '금액' }}
                width={80}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'validDate'}
                header={{ text: '유효기간' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'udate'}
                header={{ text: '회수일자' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'utime'}
                header={{ text: '회수시간' }}
                width={70}
              />
              <RGDataColumn
                fieldName={'bigo'}
                header={{ text: '비고' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'bfIssueCode'}
                header={{ text: '이전보관증번호' }}
                width={120}
              />
            </RealGridReact>
          </div>
        </div>

        <div className='mt-4 flex items-center justify-between'>
          <div className='flex items-center space-x-4'>
            <span className='text-sm text-muted-foreground'>
              총 {octData.length}건
            </span>
            {isLoading && (
              <span className='text-sm text-blue-600'>로딩 중...</span>
            )}
          </div>
        </div>
      </ExpandableCard>
    </div>
  );
}
